import React, {useCallback} from 'react';
import type {MessageDescriptor} from 'react-intl';
import {defineMessages, useIntl} from 'react-intl';
import type {KeyValueFilter} from '@igp/shared/api';
import {LocalizedText} from '@igp/shared/components/i18n';
import {EmptyLabel} from '@igp/shared/components/label';
import {HeaderCell} from '@igp/shared/components/table';
import {useUserProfiles} from '@igp/shared/features/user-profile';
import {getNonEmptyValueValidator} from '@igp/shared/redux/view';
import type {CustomTheme} from '@igp/shared/style';
import {formatDate, formatTimestamp, useNumberFormatter} from '@igp/shared/utils';
import {Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material';
import moment from 'moment';
import {makeStyles} from 'tss-react/mui';

import {useAgentLevelReportV2} from '../hooks';
import type {
    AgentLevelReportV2ServerFilterKeys,
    AgentLevelReportV2ViewModelsKeys,
    CommissionAllSubTypesHistoricalViewModel,
    QueryType,
} from '../types';

import {localized as localizedHeaders} from './DataGrid';

type RevenueShareDetailsProps = {
    rowId: string;
    agentUid: string;
    type?: QueryType;
    startDate?: number;
    endDate?: number;
};

const localized = defineMessages({
    total: {
        id: 'RevenueShareDetails_total',
        defaultMessage: 'Total Net Revenue Share',
    },
    ggr: {
        id: 'RevenueShareDetails_ggr',
        defaultMessage: 'GGR',
    },
    costs: {
        id: 'RevenueShareDetails_costs',
        defaultMessage: 'Costs',
    },
    ngr: {
        id: 'RevenueShareDetails_ngr',
        defaultMessage: 'NGR',
    },
    commissionableAmount: {
        id: 'RevenueShareDetails_commissionableAmount',
        defaultMessage: 'Commissionable Amount',
    },
    revSharePercantage: {
        id: 'RevenueShareDetails_revSharePercantage',
        defaultMessage: 'Blended Rev Share (%)',
    },
    netRevenueShare: {
        id: 'RevenueShareDetails_netRevenueShare',
        defaultMessage: 'Net Revenue Share',
    },
    revSharePercantageTooltip: {
        id: 'RevenueShareDetails_revSharePercantageTooltip',
        defaultMessage: 'Calculated as (Net Revenue Share ÷ Commissionable Amount)',
    },
    pokerCashPloTooltip: {
        id: 'RevenueShareDetails_pokerCashPloTooltip',
        defaultMessage: 'PLO revenue is included in total Poker figures—Poker Cash (NLHE SD)—for this account.',
    },
    selectedDateRange: {
        id: 'RevenueShareDetails_selectedDateRange',
        defaultMessage: 'Selected Date Range',
    },
    pokerCash: {
        id: 'RevenueShareDetails_pokerCash',
        defaultMessage: 'Poker Cash',
    },
    commissionSubtype: {
        id: 'RevenueShareDetails_commissionSubtype',
        defaultMessage: 'Commission Subtype',
    },
});

const subTypesLocalization = defineMessages<CommissionAllSubTypesHistoricalViewModel>({
    Mixed: {
        id: 'RevenueShareDetails_Mixed',
        defaultMessage: 'Mixed',
    },
    PokerNgr: {
        id: 'RevenueShareDetails_PokerNgr',
        defaultMessage: 'NGR',
    },
    PokerGgr: {
        id: 'RevenueShareDetails_PokerGgr',
        defaultMessage: 'GGR',
    },
    PokerGgrNoInsurance: {
        id: 'RevenueShareDetails_PokerGgrNoInsurance',
        defaultMessage: 'GGR (No Insurance)',
    },
    MttGgr: {
        id: 'RevenueShareDetails_MttGgr',
        defaultMessage: 'GGR',
    },
    PloGgr: {
        id: 'RevenueShareDetails_PloGgr',
        defaultMessage: 'GGR',
    },
    CasinoGgrAfterExternalCosts: {
        id: 'RevenueShareDetails_CasinoGgrAgterExternalCosts',
        defaultMessage: 'GGR (After External Costs)',
    },
    CasinoGgr: {
        id: 'RevenueShareDetails_CasinoGgr',
        defaultMessage: 'GGR',
    },
    CasinoWager: {
        id: 'RevenueShareDetails_CasinoWager',
        defaultMessage: 'Wager',
    },
    SportsWager: {
        id: 'RevenueShareDetails_SportsWager',
        defaultMessage: 'Wager',
    },
    SportsGgr: {
        id: 'RevenueShareDetails_SportsGgr',
        defaultMessage: 'GGR',
    },
    GlobalSpinsGgr: {
        id: 'RevenueShareDetails_GlobalSpinsGgr',
        defaultMessage: 'GGR',
    },
});

const useStyles = makeStyles()((theme: CustomTheme) => ({
    metaInfoTable: {
        maxWidth: '600px',
        alignSelf: 'end',
    },
    container: {
        margin: theme.spacing(2, 2, 3, 2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    cellWithTooltip: {
        display: 'flex',
        flexDirection: 'row',
        gap: theme.spacing(1),
        alignItems: 'center',
    },
    lastRow: {
        '& td': {
            border: '0',
        },
    },
}));

type RowModel = {
    rowTitle: MessageDescriptor;
    pokerCashNlheSd: string;
    pokerMtt: string;
    pokerCashPlo: string;
    casino: string;
    globalSpins: string;
    sports: string;
};

export function RevenueShareDetails({rowId, agentUid, type, startDate, endDate}: RevenueShareDetailsProps) {
    const {classes, cx} = useStyles();
    const {formatNumber} = useNumberFormatter();
    const {formatMessage} = useIntl();
    const fields: AgentLevelReportV2ViewModelsKeys[] = [
        'agentUid',
        'agentUsername',
        type === 'weekly' ? 'weekStartDateTs' : null,
        'casinoCommissionableAmount',
        'casinoCosts',
        'casinoGgr',
        'casinoNetShare',
        'casinoNgr',
        'casinoShareRatioPerc',
        'globalSpinsCommissionableAmount',
        'globalSpinsCosts',
        'globalSpinsGgr',
        'globalSpinsNetShare',
        'globalSpinsNgr',
        'globalSpinsShareRatioPerc',
        'pokerCashPloCommissionableAmount',
        'pokerCashPloCosts',
        'pokerCashPloGgr',
        'pokerCashPloNetShare',
        'pokerCashPloNgr',
        'pokerCashPloShareRatioPerc',
        'pokerMttCommissionableAmount',
        'pokerMttCosts',
        'pokerMttGgr',
        'pokerMttNetShare',
        'pokerMttNgr',
        'pokerMttShareRatioPerc',
        'pokerNlheSdCommissionableAmount',
        'pokerNlheSdCosts',
        'pokerNlheSdGgr',
        'pokerNlheSdNetShare',
        'pokerNlheSdNgr',
        'pokerNlheSdShareRatioPerc',
        'sportsCommissionableAmount',
        'sportsCosts',
        'sportsGgr',
        'sportsNetShare',
        'sportsNgr',
        'sportsShareRatioPerc',
        'totalNetShare',
        'commissionSubtypesCasino',
        'commissionSubtypesPoker',
        'commissionSubtypesPokerCashPlo',
        'commissionSubtypesPokerMtt',
        'commissionSubtypesSpins',
        'commissionSubtypesSports',
    ];
    const defaultFilters: KeyValueFilter<unknown, AgentLevelReportV2ServerFilterKeys>[] = [
        {
            key: 'id',
            value: rowId,
        },
        {
            key: 'type',
            value: type,
        },
        {
            key: 'is_downstream',
            value: true,
        },
        startDate
            ? {
                  key: 'daily_aggregation_range.from',
                  value: startDate,
              }
            : null,
        endDate
            ? {
                  key: 'daily_aggregation_range.to',
                  value: endDate,
              }
            : null,
    ];
    const item = useAgentLevelReportV2({
        viewType: 'RevenueShareDetails',
        fields: fields.filter(i => i),
        defaultFilters: defaultFilters.filter(i => i),
        saveFilters: false,
        validateFilter: filter =>
            getNonEmptyValueValidator('id')(filter) && filter?.find(f => f.key === 'type')?.value?.toString() === type,
    }).items?.[0];

    const {items: users} = useUserProfiles({
        ids: [agentUid],
        fields: ['aal'],
        viewType: 'RevenueShareDetails',
        validateFilter: () => Boolean(agentUid),
        currency: 'USD',
    });
    const isRowUser = users?.[0]?.aal_values?.isRowUser;

    const rows: RowModel[] = [
        {
            rowTitle: localized.commissionSubtype,
            pokerCashNlheSd: item?.commissionSubtypesPoker ? formatMessage(subTypesLocalization[item?.commissionSubtypesPoker]) : null,
            pokerMtt: item?.commissionSubtypesPokerMtt ? formatMessage(subTypesLocalization[item?.commissionSubtypesPokerMtt]) : null,
            pokerCashPlo: item?.commissionSubtypesPokerCashPlo
                ? formatMessage(subTypesLocalization[item?.commissionSubtypesPokerCashPlo])
                : null,
            casino: item?.commissionSubtypesCasino ? formatMessage(subTypesLocalization[item?.commissionSubtypesCasino]) : null,
            globalSpins: item?.commissionSubtypesSpins ? formatMessage(subTypesLocalization[item?.commissionSubtypesSpins]) : null,
            sports: item?.commissionSubtypesSports ? formatMessage(subTypesLocalization[item?.commissionSubtypesSports]) : null,
        },
        ...(isRowUser
            ? [
                  {
                      rowTitle: localized.ggr,
                      pokerCashNlheSd: formatNumber(item?.pokerNlheSdGgr, {format: 'USD'}),
                      pokerMtt: formatNumber(item?.pokerMttGgr, {format: 'USD'}),
                      pokerCashPlo: formatNumber(item?.pokerCashPloGgr, {format: 'USD'}),
                      casino: formatNumber(item?.casinoGgr, {format: 'USD'}),
                      globalSpins: formatNumber(item?.globalSpinsGgr, {format: 'USD'}),
                      sports: formatNumber(item?.sportsGgr, {format: 'USD'}),
                  },
                  {
                      rowTitle: localized.costs,
                      pokerCashNlheSd: formatNumber(item?.pokerNlheSdCosts, {format: 'USD'}),
                      pokerMtt: formatNumber(item?.pokerMttCosts, {format: 'USD'}),
                      pokerCashPlo: formatNumber(item?.pokerCashPloCosts, {format: 'USD'}),
                      casino: formatNumber(item?.casinoCosts, {format: 'USD'}),
                      globalSpins: formatNumber(item?.globalSpinsCosts, {format: 'USD'}),
                      sports: formatNumber(item?.sportsCosts, {format: 'USD'}),
                  },
                  {
                      rowTitle: localized.ngr,
                      pokerCashNlheSd: formatNumber(item?.pokerNlheSdNgr, {format: 'USD'}),
                      pokerMtt: formatNumber(item?.pokerMttNgr, {format: 'USD'}),
                      pokerCashPlo: formatNumber(item?.pokerCashPloNgr, {format: 'USD'}),
                      casino: formatNumber(item?.casinoNgr, {format: 'USD'}),
                      globalSpins: formatNumber(item?.globalSpinsNgr, {format: 'USD'}),
                      sports: formatNumber(item?.sportsNgr, {format: 'USD'}),
                  },
              ]
            : []),
        {
            rowTitle: localized.commissionableAmount,
            pokerCashNlheSd: formatNumber(item?.pokerNlheSdCommissionableAmount, {format: 'USD'}),
            pokerMtt: formatNumber(item?.pokerMttCommissionableAmount, {format: 'USD'}),
            pokerCashPlo: formatNumber(item?.pokerCashPloCommissionableAmount, {format: 'USD'}),
            casino: formatNumber(item?.casinoCommissionableAmount, {format: 'USD'}),
            globalSpins: formatNumber(item?.globalSpinsCommissionableAmount, {format: 'USD'}),
            sports: formatNumber(item?.sportsCommissionableAmount, {format: 'USD'}),
        },
        {
            rowTitle: localized.netRevenueShare,
            pokerCashNlheSd: formatNumber(item?.pokerNlheSdNetShare, {format: 'USD'}),
            pokerMtt: formatNumber(item?.pokerMttNetShare, {format: 'USD'}),
            pokerCashPlo: formatNumber(item?.pokerCashPloNetShare, {format: 'USD'}),
            casino: formatNumber(item?.casinoNetShare, {format: 'USD'}),
            globalSpins: formatNumber(item?.globalSpinsNetShare, {format: 'USD'}),
            sports: formatNumber(item?.sportsNetShare, {format: 'USD'}),
        },
    ];
    const getSelectedDateRangeValue = useCallback(() => {
        const startDateMoment = moment.unix(startDate).utc(false);
        const endDateMoment = moment.unix(endDate).utc(false);
        let value = formatDate(startDateMoment, 'date') + ' - ' + formatDate(endDateMoment, 'date');
        if (startDateMoment.dayOfYear() === endDateMoment.dayOfYear()) {
            value = formatDate(startDateMoment, 'date');
        }
        return value;
    }, [startDate, endDate]);

    const hasPokerCashPlo = item?.commissionSubtypesPoker === 'PokerNgr';
    return (
        <div className={classes.container}>
            <Table className={classes.metaInfoTable}>
                <TableHead>
                    <TableRow>
                        <HeaderCell>
                            <LocalizedText label={localizedHeaders.agentUid} />
                        </HeaderCell>
                        <HeaderCell>
                            <LocalizedText label={localizedHeaders.agentUsername} />
                        </HeaderCell>
                        <HeaderCell>
                            <LocalizedText label={type === 'daily' ? localized.selectedDateRange : localizedHeaders.weekStartDateTs} />
                        </HeaderCell>
                        <HeaderCell>
                            <LocalizedText label={localized.total} />
                        </HeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow className={classes.lastRow}>
                        <TableCell>{item?.agentUid}</TableCell>
                        <TableCell>{item?.agentUsername}</TableCell>
                        <TableCell>
                            {type === 'daily' ? (
                                startDate && endDate ? (
                                    getSelectedDateRangeValue()
                                ) : (
                                    <EmptyLabel defaultSymbol={'dash'} />
                                )
                            ) : item?.weekStartDateTs ? (
                                formatTimestamp(item?.weekStartDateTs, 'date')
                            ) : (
                                <EmptyLabel defaultSymbol={'dash'} />
                            )}
                        </TableCell>
                        <RowCell value={formatNumber(item?.totalNetShare, {format: 'USD'})} />
                    </TableRow>
                </TableBody>
            </Table>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <HeaderCell>
                            <LocalizedText label={hasPokerCashPlo ? localizedHeaders.pokerNlheSdNetShare : localized.pokerCash} />
                        </HeaderCell>
                        <HeaderCell>
                            <LocalizedText label={localizedHeaders.pokerMttNetShare} />
                        </HeaderCell>
                        {hasPokerCashPlo && (
                            <HeaderCell>
                                <LocalizedText label={localizedHeaders.pokerCashPloNetShare} />
                            </HeaderCell>
                        )}
                        <HeaderCell>
                            <LocalizedText label={localizedHeaders.casinoNetShare} />
                        </HeaderCell>
                        <HeaderCell>
                            <LocalizedText label={localizedHeaders.globalSpinsNetShare} />
                        </HeaderCell>
                        <HeaderCell>
                            <LocalizedText label={localizedHeaders.sportsNetShare} />
                        </HeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, i, arr) => {
                        return (
                            <TableRow key={i} className={cx(arr.length - 1 === i && classes.lastRow)}>
                                <HeaderCell>
                                    <LocalizedText label={r.rowTitle} />
                                </HeaderCell>
                                <RowCell value={r.pokerCashNlheSd} />
                                <RowCell value={r.pokerMtt} />
                                {hasPokerCashPlo && <RowCell value={r.pokerCashPlo} />}
                                <RowCell value={r.casino} />
                                <RowCell value={r.globalSpins} />
                                <RowCell value={r.sports} />
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}

type RowCellProps = {
    value: string;
};

function RowCell({value}: RowCellProps) {
    return <TableCell>{value ?? <EmptyLabel defaultSymbol={'dash'} />}</TableCell>;
}
